import React, { useState, useMemo } from 'react';
import { Table, TableColumn, Pagination } from '@components/common/table';
import { Button } from '@components/common/button';
import { Input } from '@components/common/input';
import {
  Plus,
  Search,
  Filter,
  Download,
  Eye,
  Edit,
  Send,
  AlertCircle,
  FileText,
  DollarSign,
  CheckCircle,
  Clock,
} from 'lucide-react';
import { Invoice, InvoiceStatus } from '@/types/invoice_types';

interface InvoiceListProps {
  invoices: Invoice[];
  is_loading?: boolean;
  on_add_invoice: () => void;
  on_view_invoice: (invoice: Invoice) => void;
  on_edit_invoice: (invoice: Invoice) => void;
  on_send_invoice: (invoice: Invoice) => void;
  on_download_invoice: (invoice: Invoice) => void;
  on_export?: () => void;
}

export const InvoiceList: React.FC<InvoiceListProps> = ({
  invoices,
  is_loading = false,
  on_add_invoice,
  on_view_invoice,
  on_edit_invoice,
  on_send_invoice,
  on_download_invoice,
  on_export
}) => {
  const [search_term, set_search_term] = useState('');
  const [filter_status, set_filter_status] = useState<InvoiceStatus | 'all'>('all');
  const [current_page, set_current_page] = useState(1);
  const [sort_config, set_sort_config] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  }>({ key: 'issue_date', direction: 'desc' });

  const items_per_page = 10;

  // Helper to normalize status (backend returns uppercase, frontend might use lowercase)
  const normalize_status = (status: string) => status?.toUpperCase();

  // Helper to get payment status
  const get_payment_status = (invoice: Invoice) => {
    const balance = invoice.balance_due || (invoice as any).amount_outstanding || 0;
    const paid = invoice.amount_paid || 0;
    const total = invoice.total_amount || 0;

    if (balance < 0) return 'overpaid';
    if (balance === 0 || paid >= total) return 'paid';
    if (paid > 0) return 'partial';
    return 'unpaid';
  };

  // Filter and search invoices
  const filtered_invoices = useMemo(() => {
    let result = [...invoices];

    // FIXED: Case-insensitive status comparison
    if (filter_status !== 'all') {
      result = result.filter(invoice => 
        normalize_status(invoice.status) === normalize_status(filter_status)
      );
    }

    if (search_term) {
      const term = search_term.toLowerCase();
      result = result.filter(invoice =>
        invoice.invoice_number.toLowerCase().includes(term) ||
        invoice.client_name.toLowerCase().includes(term) ||
        invoice.meter_number?.toLowerCase().includes(term)
      );
    }

    result.sort((a, b) => {
      const a_value = a[sort_config.key as keyof Invoice];
      const b_value = b[sort_config.key as keyof Invoice];

      if (a_value === undefined || b_value === undefined) return 0;

      if (a_value < b_value) return sort_config.direction === 'asc' ? -1 : 1;
      if (a_value > b_value) return sort_config.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [invoices, filter_status, search_term, sort_config]);

  const total_pages = Math.ceil(filtered_invoices.length / items_per_page);
  const paginated_invoices = filtered_invoices.slice(
    (current_page - 1) * items_per_page,
    current_page * items_per_page
  );

  const handle_sort = (key: string) => {
    set_sort_config(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const is_overdue = (invoice: Invoice) => {
    const normalized_status = normalize_status(invoice.status);
    if (normalized_status === 'PAID' || normalized_status === 'CANCELLED') return false;
    const payment_status = get_payment_status(invoice);
    if (payment_status === 'paid') return false;
    return new Date(invoice.due_date) < new Date();
  };

  const days_until_due = (invoice: Invoice) => {
    const days = Math.ceil(
      (new Date(invoice.due_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
    );
    return days;
  };

  const format_currency = (amount: number) => {
    return `KES ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const get_payment_badge = (invoice: Invoice) => {
    const payment_status = get_payment_status(invoice);
    
    const badges = {
      paid: {
        bg: 'bg-success bg-opacity-10',
        text: 'text-success',
        icon: <CheckCircle size={14} />,
        label: 'Paid'
      },
      partial: {
        bg: 'bg-warning bg-opacity-10',
        text: 'text-warning',
        icon: <Clock size={14} />,
        label: 'Partial'
      },
      unpaid: {
        bg: 'bg-gray-100',
        text: 'text-gray-600',
        icon: <AlertCircle size={14} />,
        label: 'Unpaid'
      },
      overpaid: {
        bg: 'bg-info bg-opacity-10',
        text: 'text-info',
        icon: <CheckCircle size={14} />,
        label: 'Overpaid'
      }
    };

    const badge = badges[payment_status];

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${badge.bg} ${badge.text}`}>
        {badge.icon}
        {badge.label}
      </span>
    );
  };

  const columns: TableColumn<Invoice>[] = [
    {
      key: 'invoice_number',
      header: 'Invoice #',
      sortable: true,
      render: (invoice) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-forest-green bg-opacity-10 text-forest-green flex items-center justify-center">
            <FileText size={20} />
          </div>
          <div>
            <p className="font-semibold text-charcoal">{invoice.invoice_number}</p>
            {invoice.meter_number && (
              <p className="text-xs text-gray-500">Meter: {invoice.meter_number}</p>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'client_name',
      header: 'Client',
      sortable: true,
      render: (invoice) => (
        <div>
          <p className="font-medium text-charcoal">{invoice.client_name}</p>
          <p className="text-xs text-gray-500">
            {new Date(invoice.billing_period_start).toLocaleDateString()} - {new Date(invoice.billing_period_end).toLocaleDateString()}
          </p>
        </div>
      )
    },
    {
      key: 'issue_date',
      header: 'Issue Date',
      sortable: true,
      render: (invoice) => (
        <div className="text-sm">
          <p className="text-charcoal">{new Date(invoice.issue_date).toLocaleDateString()}</p>
        </div>
      )
    },
    {
      key: 'due_date',
      header: 'Due Date',
      sortable: true,
      render: (invoice) => {
        const days = days_until_due(invoice);
        const overdue = is_overdue(invoice);
        const payment_status = get_payment_status(invoice);

        return (
          <div className="text-sm">
            <p className={overdue ? 'text-error font-medium' : 'text-charcoal'}>
              {new Date(invoice.due_date).toLocaleDateString()}
            </p>
            {payment_status !== 'paid' && payment_status !== 'overpaid' && (
              <p className={`text-xs ${overdue ? 'text-error' : days <= 7 ? 'text-warning' : 'text-gray-500'}`}>
                {overdue ? `${Math.abs(days)} days overdue` : `${days} days left`}
              </p>
            )}
          </div>
        );
      }
    },
    {
      key: 'total_amount',
      header: 'Amount',
      sortable: true,
      align: 'right',
      render: (invoice) => {
        const balance = invoice.balance_due || (invoice as any).amount_outstanding || 0;
        const payment_status = get_payment_status(invoice);

        return (
          <div className="text-right">
            <p className="font-semibold text-charcoal">
              {format_currency(invoice.total_amount)}
            </p>
            {payment_status === 'paid' ? (
              <p className="text-xs text-success">
                ✓ Paid in full
              </p>
            ) : payment_status === 'partial' ? (
              <p className="text-xs text-warning">
                Due: {format_currency(balance)}
              </p>
            ) : balance > 0 && normalize_status(invoice.status) !== 'CANCELLED' ? (
              <p className="text-xs text-error">
                Due: {format_currency(balance)}
              </p>
            ) : null}
          </div>
        );
      }
    },
    {
      key: 'payment_status',
      header: 'Payment',
      sortable: false,
      render: (invoice) => get_payment_badge(invoice)
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (invoice) => {
        const normalized_status = normalize_status(invoice.status);
        
        return (
          <div className="flex items-center justify-end gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                on_view_invoice(invoice);
              }}
              icon_left={<Eye size={14} />}
              className="text-gray-600"
            >
              View
            </Button>
            {(normalized_status === 'DRAFT' || normalized_status === 'SENT') && (
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  on_edit_invoice(invoice);
                }}
                icon_left={<Edit size={14} />}
                className="text-forest-green"
              >
                Edit
              </Button>
            )}
            {normalized_status === 'DRAFT' && (
              <Button
                size="sm"
                variant="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  on_send_invoice(invoice);
                }}
                icon_left={<Send size={14} />}
                className="text-xs"
              >
                Send
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                on_download_invoice(invoice);
              }}
              icon_left={<Download size={14} />}
              className="text-blue-600"
            >
              PDF
            </Button>
          </div>
        );
      }
    }
  ];

  // FIXED: Calculate stats with case-insensitive comparison
  const stats = {
    total: filtered_invoices.length,
    draft: filtered_invoices.filter(i => normalize_status(i.status) === 'DRAFT').length,
    sent: filtered_invoices.filter(i => normalize_status(i.status) === 'SENT').length,
    paid: filtered_invoices.filter(i => get_payment_status(i) === 'paid').length,
    overdue: filtered_invoices.filter(i => is_overdue(i)).length,
    total_amount: filtered_invoices.reduce((sum, i) => sum + i.total_amount, 0),
    outstanding: filtered_invoices.reduce((sum, i) => {
      const payment_status = get_payment_status(i);
      if (payment_status === 'paid' || payment_status === 'overpaid') return sum;
      return sum + (i.balance_due || (i as any).amount_outstanding || 0);
    }, 0)
  };

  return (
    <div className="space-y-4">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex-1 w-full sm:max-w-md">
          <Input
            type="text"
            placeholder="Search by invoice #, client, meter..."
            value={search_term}
            onChange={(e) => set_search_term(e.target.value)}
            icon_left={<Search size={18} />}
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-gray-300">
            <Filter size={16} className="text-gray-500" />
            <select
              value={filter_status}
              onChange={(e) => set_filter_status(e.target.value as InvoiceStatus | 'all')}
              className="text-sm border-none focus:outline-none cursor-pointer"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {on_export && (
            <Button
              variant="outline"
              size="md"
              icon_left={<Download size={18} />}
              onClick={on_export}
            >
              Export
            </Button>
          )}

          <Button
            variant="primary"
            size="md"
            icon_left={<Plus size={18} />}
            onClick={on_add_invoice}
          >
            Create Invoice
          </Button>
        </div>
      </div>

      {/* Compact Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        <div className="bg-white p-3 rounded-lg border border-light-gray">
          <p className="text-xs text-medium-gray mb-0.5">Total</p>
          <p className="text-xl font-bold text-charcoal">{stats.total}</p>
        </div>
        <div className="bg-white p-3 rounded-lg border border-light-gray">
          <p className="text-xs text-medium-gray mb-0.5">Draft</p>
          <p className="text-xl font-bold text-gray-600">{stats.draft}</p>
        </div>
        <div className="bg-white p-3 rounded-lg border border-light-gray">
          <p className="text-xs text-medium-gray mb-0.5">Sent</p>
          <p className="text-xl font-bold text-info">{stats.sent}</p>
        </div>
        <div className="bg-white p-3 rounded-lg border border-light-gray">
          <p className="text-xs text-medium-gray mb-0.5">Paid</p>
          <p className="text-xl font-bold text-success">{stats.paid}</p>
        </div>
        <div className="bg-white p-3 rounded-lg border border-light-gray">
          <p className="text-xs text-medium-gray mb-0.5">Overdue</p>
          <p className="text-xl font-bold text-error">{stats.overdue}</p>
        </div>
        <div className="bg-white p-3 rounded-lg border border-light-gray">
          <div className="flex items-center gap-1 mb-0.5">
            <DollarSign size={12} className="text-medium-gray" />
            <p className="text-xs text-medium-gray">Total</p>
          </div>
          <p className="text-base font-bold text-charcoal">
            {(stats.total_amount / 1000).toFixed(1)}K
          </p>
        </div>
        <div className="bg-white p-3 rounded-lg border border-light-gray">
          <div className="flex items-center gap-1 mb-0.5">
            <AlertCircle size={12} className="text-error" />
            <p className="text-xs text-medium-gray">Due</p>
          </div>
          <p className="text-base font-bold text-error">
            {(stats.outstanding / 1000).toFixed(1)}K
          </p>
        </div>
      </div>

      {/* Table */}
      <Table
        data={paginated_invoices}
        columns={columns}
        is_loading={is_loading}
        on_row_click={on_view_invoice}
        sort_config={sort_config}
        on_sort={handle_sort}
        empty_message="No invoices found. Create your first invoice to get started!"
        striped
        hoverable
      />

      {/* Pagination */}
      {total_pages > 1 && (
        <Pagination
          current_page={current_page}
          total_pages={total_pages}
          on_page_change={set_current_page}
          items_per_page={items_per_page}
          total_items={filtered_invoices.length}
        />
      )}
    </div>
  );
};